import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { ConfirmModal } from '../ConfirmModal'
import { Modal } from '../Modal'
import {
  labelRefeicao,
  nutrientesDaPorcao,
  somarNutrientes,
  type NutrientesTotais,
  type RefeicaoFicha,
} from '../../lib/fichaTecnicaPacote'
import {
  alimentoComTabelaPrecos,
  alimentosSemTabelaPrecos,
  filtrarAlimentosTabelaPrecos,
  mensagemAlimentosIndisponiveis,
} from '../../lib/alimentosTabelaPrecos'
import { formatNumber } from '../../lib/utils'
import { supabase } from '../../lib/supabase'
import type { AlimentoMacronutriente, Prato, PratoItem } from '../../types/database'
import { REFEICOES } from '../../types/database'

type ItemDraft = {
  key: string
  alimento_id: string
  quantidade_g: string
}

type FormState = {
  nome: string
  refeicao: RefeicaoFicha
  descritivo: string
  itens: ItemDraft[]
}

function newKey() {
  return `tmp-${Math.random().toString(36).slice(2, 9)}`
}

function emptyItem(): ItemDraft {
  return { key: newKey(), alimento_id: '', quantidade_g: '0' }
}

function emptyForm(refeicao: RefeicaoFicha = 'almoco'): FormState {
  return { nome: '', refeicao, descritivo: '', itens: [emptyItem()] }
}

const NUTRI_COLS: { key: keyof NutrientesTotais; label: string }[] = [
  { key: 'energia_kcal', label: 'kcal' },
  { key: 'proteinas_g', label: 'PTN' },
  { key: 'carboidratos_g', label: 'CHO' },
  { key: 'lipideos_g', label: 'LIP' },
]

type Props = {
  onError: (msg: string | null) => void
  onInfo: (msg: string | null) => void
}

export function PratosSection({ onError, onInfo }: Props) {
  const { canWrite, canDelete } = useAuth()
  const writable = canWrite('pratos')
  const [filtroRef, setFiltroRef] = useState<RefeicaoFicha | 'todas'>('todas')
  const [busca, setBusca] = useState('')
  const [pratos, setPratos] = useState<Prato[]>([])
  const [itensByPrato, setItensByPrato] = useState<Record<string, PratoItem[]>>({})
  const [alimentos, setAlimentos] = useState<AlimentoMacronutriente[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Prato | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [buscaAlimento, setBuscaAlimento] = useState<Record<string, string>>({})

  const alimentosById = useMemo(() => {
    const m = new Map<string, AlimentoMacronutriente>()
    for (const a of alimentos) m.set(a.id, a)
    return m
  }, [alimentos])

  const alimentosCatalogo = useMemo(() => filtrarAlimentosTabelaPrecos(alimentos), [alimentos])

  const alimentosIndisponiveisForm = useMemo(
    () => alimentosSemTabelaPrecos(form.itens.map((it) => it.alimento_id), alimentosById),
    [form.itens, alimentosById],
  )

  const avisoIndisponiveisForm = useMemo(
    () => mensagemAlimentosIndisponiveis(alimentosIndisponiveisForm.map((a) => a.nome)),
    [alimentosIndisponiveisForm],
  )

  const load = useCallback(async () => {
    setLoading(true)
    onError(null)
    const [prRes, alRes] = await Promise.all([
      supabase.from('pratos').select('*').order('refeicao').order('nome'),
      supabase.from('alimentos_macronutrientes').select('*').order('nome'),
    ])
    if (prRes.error) {
      onError(prRes.error.message)
      setLoading(false)
      return
    }
    if (alRes.error) onError(alRes.error.message)
    const list = (prRes.data as Prato[]) || []
    setPratos(list)
    setAlimentos((alRes.data as AlimentoMacronutriente[]) || [])

    const ids = list.map((p) => p.id)
    if (ids.length) {
      const itRes = await supabase.from('prato_itens').select('*').in('prato_id', ids).order('ordem')
      if (itRes.error) onError(itRes.error.message)
      else {
        const map: Record<string, PratoItem[]> = {}
        for (const it of (itRes.data as PratoItem[]) || []) {
          ;(map[it.prato_id] ||= []).push(it)
        }
        setItensByPrato(map)
      }
    } else setItensByPrato({})
    setLoading(false)
  }, [onError])

  useEffect(() => {
    void load()
  }, [load])

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase()
    return pratos.filter((p) => {
      if (filtroRef !== 'todas' && p.refeicao !== filtroRef) return false
      if (q && !p.nome.toLowerCase().includes(q)) return false
      return true
    })
  }, [pratos, filtroRef, busca])

  function totaisPrato(pratoId: string): NutrientesTotais {
    const itens = itensByPrato[pratoId] || []
    return somarNutrientes(
      itens.map((it) => nutrientesDaPorcao(alimentosById.get(it.alimento_id), Number(it.quantidade_g) || 0)),
    )
  }

  function openNew() {
    setEditing(null)
    setForm(emptyForm(filtroRef === 'todas' ? 'almoco' : filtroRef))
    setBuscaAlimento({})
    setModalOpen(true)
  }

  function openEdit(p: Prato) {
    const itens = itensByPrato[p.id] || []
    setEditing(p)
    setForm({
      nome: p.nome,
      refeicao: p.refeicao as RefeicaoFicha,
      descritivo: p.descritivo || '',
      itens: itens.length
        ? itens.map((it) => ({
            key: it.id,
            alimento_id: it.alimento_id,
            quantidade_g: String(it.quantidade_g ?? 0),
          }))
        : [emptyItem()],
    })
    setBuscaAlimento({})
    setModalOpen(true)
  }

  async function save() {
    if (!writable) return
    if (!form.nome.trim()) {
      onError('Nome do prato é obrigatório')
      return
    }
    const itensValidos = form.itens.filter((it) => it.alimento_id)
    setSaving(true)
    onError(null)
    try {
      const payload = {
        nome: form.nome.trim(),
        refeicao: form.refeicao,
        descritivo: form.descritivo.trim(),
        atualizado_em: new Date().toISOString(),
      }
      let pratoId = editing?.id
      if (editing) {
        const { error } = await supabase.from('pratos').update(payload).eq('id', editing.id)
        if (error) throw new Error(error.message)
      } else {
        const { data, error } = await supabase.from('pratos').insert(payload).select('id').single()
        if (error) throw new Error(error.message)
        pratoId = data.id as string
      }
      if (!pratoId) throw new Error('Prato sem id')

      const { error: delErr } = await supabase.from('prato_itens').delete().eq('prato_id', pratoId)
      if (delErr) throw new Error(delErr.message)
      if (itensValidos.length) {
        const { error: insErr } = await supabase.from('prato_itens').insert(
          itensValidos.map((it, idx) => ({
            prato_id: pratoId,
            alimento_id: it.alimento_id,
            quantidade_g: Number(String(it.quantidade_g).replace(',', '.')) || 0,
            ordem: idx,
          })),
        )
        if (insErr) throw new Error(insErr.message)
      }
      setModalOpen(false)
      onInfo(editing ? 'Prato atualizado.' : 'Prato cadastrado.')
      await load()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Falha ao salvar prato')
    } finally {
      setSaving(false)
    }
  }

  async function confirmDelete() {
    if (!deleteId || !canDelete('pratos')) return
    const { error } = await supabase.from('pratos').delete().eq('id', deleteId)
    setDeleteId(null)
    if (error) onError(error.message)
    else {
      onInfo('Prato excluído.')
      await load()
    }
  }

  if (loading) return <div className="loading">Carregando pratos…</div>

  return (
    <div className="card">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 8,
        }}
      >
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Banco de pratos e preparações oficiais</h2>
        {writable && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={openNew}>
            + Novo prato
          </button>
        )}
      </div>
      <p className="kpi-sub" style={{ marginBottom: 14 }}>
        Pratos padronizados pela Responsável Técnica com fichas de preparo e lista de ingredientes.
      </p>

      <div className="page-actions" style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Refeição</label>
          <select
            value={filtroRef}
            onChange={(e) => setFiltroRef(e.target.value as RefeicaoFicha | 'todas')}
          >
            <option value="todas">Todas</option>
            {REFEICOES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 220, flex: '1 1 200px', maxWidth: 360 }}>
          <label>Buscar</label>
          <input
            type="search"
            placeholder="Nome do prato…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 16,
        }}
      >
        {filtrados.map((p) => {
          const tot = totaisPrato(p.id)
          return (
            <div
              key={p.id}
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: 10,
                padding: 14,
                background: '#fff',
              }}
            >
              <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 2 }}>{p.nome}</div>
              <div style={{ fontSize: 11, color: 'var(--color-primary, #1b5e20)', fontWeight: 700, marginBottom: 6 }}>
                Refeição: {labelRefeicao(p.refeicao)} · {formatNumber(tot.energia_kcal, 0)} kcal
              </div>
              <p className="kpi-sub" style={{ marginBottom: 12, lineHeight: 1.4 }}>
                {p.descritivo || 'Sem modo de preparo'}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {writable && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}>
                    Ver / editar prato
                  </button>
                )}
                {canDelete('pratos') && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setDeleteId(p.id)}
                  >
                    Excluir
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {!filtrados.length && (
        <p className="kpi-sub" style={{ marginTop: 16 }}>
          Nenhum prato cadastrado.
        </p>
      )}

      <Modal
        open={modalOpen}
        title={editing ? 'Editar prato' : 'Novo prato'}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Nome *</label>
            <input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          </div>
          <div className="field">
            <label>Refeição</label>
            <select
              value={form.refeicao}
              onChange={(e) => setForm({ ...form, refeicao: e.target.value as RefeicaoFicha })}
            >
              {REFEICOES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Modo de Preparo</label>
            <textarea
              rows={3}
              value={form.descritivo}
              onChange={(e) => setForm({ ...form, descritivo: e.target.value })}
              placeholder="Insira o passo a passo de preparo do prato para orientação das cozinheiras escolares..."
            />
          </div>
        </div>

        <h3 style={{ fontSize: '1rem', marginTop: 16 }}>Alimentos (por 100 g × quantidade)</h3>
        {avisoIndisponiveisForm && (
          <div className="alert alert-warning" style={{ marginBottom: 12 }}>
            {avisoIndisponiveisForm}
          </div>
        )}
        {form.itens.map((it, idx) => {
          const al = alimentosById.get(it.alimento_id)
          const indisponivel = it.alimento_id && !alimentoComTabelaPrecos(al)
          const nut = nutrientesDaPorcao(al, Number(it.quantidade_g) || 0)
          const q = (buscaAlimento[it.key] || '').toLowerCase()
          const opts = q
            ? alimentosCatalogo.filter((a) => a.nome.toLowerCase().includes(q)).slice(0, 60)
            : alimentosCatalogo.slice(0, 60)
          return (
            <div key={it.key} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <input
                list={`prato-alim-${it.key}`}
                placeholder="Alimento…"
                style={{ flex: '2 1 200px' }}
                value={buscaAlimento[it.key] ?? (al?.nome || '')}
                onChange={(e) => {
                  const v = e.target.value
                  setBuscaAlimento((prev) => ({ ...prev, [it.key]: v }))
                  const match = alimentosCatalogo.find((a) => a.nome.toLowerCase() === v.toLowerCase())
                  setForm({
                    ...form,
                    itens: form.itens.map((row, i) =>
                      i === idx ? { ...row, alimento_id: match?.id || '' } : row,
                    ),
                  })
                }}
              />
              <datalist id={`prato-alim-${it.key}`}>
                {opts.map((a) => (
                  <option key={a.id} value={a.nome} />
                ))}
              </datalist>
              {indisponivel && (
                <span className="kpi-sub" style={{ alignSelf: 'center', color: 'var(--color-warning, #b45309)' }}>
                  Indisponível na Tabela de Preços
                </span>
              )}
              <input
                type="number"
                min={0}
                step="any"
                style={{ width: 90 }}
                value={it.quantidade_g}
                onChange={(e) =>
                  setForm({
                    ...form,
                    itens: form.itens.map((row, i) =>
                      i === idx ? { ...row, quantidade_g: e.target.value } : row,
                    ),
                  })
                }
              />
              <span className="kpi-sub" style={{ alignSelf: 'center' }}>
                {NUTRI_COLS.map((c) => `${c.label} ${formatNumber(nut[c.key], 1)}`).join(' · ')}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() =>
                  setForm({
                    ...form,
                    itens: form.itens.length > 1 ? form.itens.filter((_, i) => i !== idx) : [emptyItem()],
                  })
                }
              >
                Remover
              </button>
            </div>
          )
        })}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setForm({ ...form, itens: [...form.itens, emptyItem()] })}
        >
          Adicionar alimento
        </button>
      </Modal>

      <ConfirmModal
        open={!!deleteId}
        title="Excluir prato"
        description="Remover este prato do catálogo?"
        confirmLabel="Excluir"
        variant="danger"
        onClose={() => setDeleteId(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  )
}
